import { useState, useEffect } from "react";
import {
  Download,
  Zap,
  TrendingUp,
  Users,
  MapPin,
  Shield,
  ArrowRight,
} from "lucide-react";
import BG from "../../Assets/HeroBackground10.png";
import bg_img from "../../Assets/bg_img.webp";
import yesbank from "../../Assets/yesbnk.png";

const HeroSection = () => {
  const [counts, setCounts] = useState({
    transactions: 0,
    agents: 0,
    services: 0,
    cities: 0,
  });

  useEffect(() => {
    const targets = {
      transactions: 5000,
      agents: 1000,
      services: 50,
      cities: 20,
    };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setCounts({
          transactions: Math.min(
            Math.floor((targets.transactions / steps) * currentStep),
            targets.transactions,
          ),
          agents: Math.min(
            Math.floor((targets.agents / steps) * currentStep),
            targets.agents,
          ),
          services: Math.min(
            Math.floor((targets.services / steps) * currentStep),
            targets.services,
          ),
          cities: Math.min(
            Math.floor((targets.cities / steps) * currentStep),
            targets.cities,
          ),
        });
      } else {
        clearInterval(timer);
      }
    }, interval);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-slate-50 font-inter flex flex-col">
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <img
          src={BG}
          alt=""
          className="w-full h-full object-cover opacity-30 lg:opacity-40"
        />
        {/* <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white"></div> */}
        <div className="absolute top-[-5%] right-[-10%] w-[300px] lg:w-[600px] h-[300px] lg:h-[600px]  rounded-full animate-pulse" />
        <div className="absolute bottom-[10%] left-[-10%] w-[300px] lg:w-[600px] h-[300px] lg:h-[600px] bg-blue-400/10 blur-[100px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-10 lg:pt-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-6 lg:space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mx-auto lg:mx-0">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-pink-500"></span>
              </span>
              <span className="text-slate-600 text-[10px] lg:text-xs font-bold uppercase tracking-widest">
                Bumppy Payments • Next-Gen Banking
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-black text-[#000856] leading-[1.1]">
              Empowering your <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-orange-500 to-pink-600">
                financial future
              </span>
            </h1>

            <p className="text-base lg:text-lg text-slate-600 leading-relaxed max-w-lg mx-auto lg:mx-0">
              Join the digital revolution with secure, transparent, and modern
              banking services. Transform your shop into a digital hub to{" "}
              <span className="text-blue-600 font-bold italic">
                earn & prosper
              </span>
              .
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-4">
              <a href="https://play.google.com/store/apps/details?id=com.app.bumppypayments">
                <button className="w-full sm:w-auto bg-indigo-600 hover:bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 hover:scale-105 transition-all shadow-xl ">
                  <Download className="w-5 h-5" />
                  <span>Download App</span>
                </button>
              </a>
              <a href="https://dashboard.bumppy.com/register">
                <button className="w-full sm:w-auto bg-white  text-indigo-600  border-2 border-slate-200 px-8 py-4 rounded-2xl font-bold hover:border-indigo-600 transition-all shadow-sm flex items-center justify-center gap-2">
                  Sign Up <ArrowRight className="w-5 h-5" />
                </button>
              </a>
            </div>
          </div>

          <div className="relative flex justify-center items-center group">
            <div className="absolute w-[80%] h-[80%]" />
            <div className="animate-float max-w-[280px] sm:max-w-[350px] lg:max-w-[480px]">
              <img
                src={bg_img}
                alt="Mockup"
                className="w-full h-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-20 w-full max-w-7xl mx-auto px-6 mt-16 lg:mt-24 mb-[-60px] lg:mb-[-80px]">
        <div className="bg-white/95 backdrop-blur-xl rounded-[1.5rem] lg:rounded-[3rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white p-6 lg:p-10 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {[
            {
              label: "Daily Transactions",
              val: counts.transactions,
              icon: <Zap size={20} />,
              color: "from-pink-500 to-orange-500",
            },
            {
              label: "Active Agents",
              val: counts.agents,
              icon: <Users size={20} />,
              color: "from-blue-500 to-indigo-600",
            },
            {
              label: "Total Services",
              val: counts.services,
              icon: <TrendingUp size={20} />,
              color: "from-purple-500 to-pink-600",
            },
            {
              label: "Cities Covered",
              val: counts.cities,
              icon: <MapPin size={20} />,
              color: "from-emerald-500 to-teal-600",
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="text-center group md:border-r border-slate-100 last:border-0 px-2"
            >
              <div
                className={`inline-flex items-center justify-center w-10 h-10 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-gradient-to-br ${stat.color} text-white mb-3 shadow-lg group-hover:scale-110 transition-transform`}
              >
                {stat.icon}
              </div>
              <div className="text-2xl lg:text-4xl font-black text-slate-900 tracking-tighter">
                {stat.val.toLocaleString()}+
              </div>
              <div className="text-slate-400 text-[9px] lg:text-[11px] font-bold uppercase tracking-widest mt-1">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .animate-float { animation: float 5s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default HeroSection;
